import type { Chiave, Traduttore } from "../../i18n/index.ts";
import { useT } from "../lib/i18n.tsx";
import { useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { api } from "../lib/api.ts";
import { streamEvents } from "../lib/sse.ts";
import { useAction, useQuery } from "../lib/hooks.ts";
import { segmentaAnsi } from "../lib/log-ansi.ts";
import {
  allineaSerie,
  coloreMarcatore,
  coloreSerie,
  finestraRapida,
  riepilogoLive,
  type Metrica,
  type SceltaRapida,
  type StatoPagina,
} from "../lib/risorse.ts";
import type { AvvisiStack, EventoContainer, Punto, Soglie } from "../lib/types.ts";
import { TestoAnsi } from "./TestoLog.tsx";
import { Button, Card, Empty, ErrorNotice, Spinner, cx } from "./ui.tsx";

/**
 * Consumo di risorse di uno stack: chi ha saturato CPU o memoria, quando, e
 * cosa diceva in quel momento.
 *
 * Grafici, tabella e log leggono la stessa finestra e lo stesso servizio, che
 * vivono nell'URL (vedi `StatoPagina`): il link di un'email di avviso apre la
 * scheda già posizionata.
 */

interface RispostaRisorse {
  secchio: number;
  serie: Record<string, Punto[]>;
  eventi: EventoContainer[];
  riepilogo: {
    serie: string;
    cpuMax: number | null;
    memPctMax: number;
    rxMax: number | null;
    txMax: number | null;
    eventi: number;
    sopraSoglia: boolean;
  }[];
  soglie: Soglie;
}

const ora = () => Math.floor(Date.now() / 1000);

/** Quanto dura il live: lo stesso tetto del server, che poi chiude il flusso. */
const LIVE_DURATA = 5 * 60;

const GRAFICI: { metrica: Metrica; titolo: Chiave; unità: string }[] = [
  { metrica: "cpu", titolo: "risorse.graficoCpu", unità: "%" },
  { metrica: "memoria", titolo: "risorse.graficoMemoria", unità: "%" },
  { metrica: "rete", titolo: "risorse.graficoRete", unità: "KB/s" },
];

const numero = (v: number | null, unità: string, t: Traduttore) =>
  v === null ? "—" : `${t.numero(v, { maximumFractionDigits: v < 10 ? 1 : 0 })} ${unità}`;

/** Secondi epoch nel formato di `<input type="datetime-local">`, nel fuso del browser. */
function perInput(secondi: number): string {
  const d = new Date(secondi * 1000);
  const due = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${due(d.getMonth() + 1)}-${due(d.getDate())}T${due(d.getHours())}:${due(d.getMinutes())}`;
}

const orario = (secondi: number, t: Traduttore) => t.data(secondi * 1000);

/** Etichetta dell'asse dei tempi: con più di un giorno serve anche la data. */
function etichettaTempo(secondi: number, durata: number, lingua: string): string {
  const d = new Date(secondi * 1000);
  const ora = d.toLocaleTimeString(lingua, { hour: "2-digit", minute: "2-digit" });
  return durata > 86400 ? `${d.toLocaleDateString(lingua, { day: "2-digit", month: "2-digit" })} ${ora}` : ora;
}

/** Il tema corrente; i grafici disegnano su canvas e non seguono il CSS da soli. */
function useTemaScuro(): boolean {
  const leggi = () => document.documentElement.dataset.tema === "scuro";
  const [scuro, setScuro] = useState(leggi);
  useEffect(() => {
    const osservatore = new MutationObserver(() => setScuro(leggi()));
    osservatore.observe(document.documentElement, { attributes: true, attributeFilter: ["data-tema"] });
    return () => osservatore.disconnect();
  }, []);
  return scuro;
}

export function SchedaRisorse({
  stackId,
  stato,
  onCambia,
  puòScrivere,
}: {
  stackId: number;
  stato: StatoPagina;
  onCambia: (parziale: Partial<StatoPagina>) => void;
  puòScrivere: boolean;
}) {
  // Senza una finestra nell'URL, l'ultima ora fino all'apertura della scheda.
  const [predefinita] = useState(() => finestraRapida("1h", ora()));
  // Il live: punti letti da Docker ogni 2 s finché è `attivo`; finito, restano a schermo.
  const [live, setLive] = useState<{ inizio: number; serie: Record<string, Punto[]>; attivo: boolean } | null>(null);
  const [erroreLive, setErroreLive] = useState<unknown>(null);
  const liveAttivo = live?.attivo ?? false;
  const inizioLive = live?.inizio;
  useEffect(() => {
    if (!liveAttivo) return;
    const ferma = new AbortController();
    void (async () => {
      try {
        for await (const { data } of streamEvents<Record<string, Punto>>(`/stacks/${stackId}/risorse/live`, {
          method: "GET",
          signal: ferma.signal,
        })) {
          setLive((l) => {
            if (!l) return l;
            const serie = { ...l.serie };
            for (const [nome, punto] of Object.entries(data)) serie[nome] = [...(serie[nome] ?? []), punto];
            return { ...l, serie };
          });
        }
      } catch (causa) {
        if (!ferma.signal.aborted) setErroreLive(causa);
      }
      if (!ferma.signal.aborted) setLive((l) => l && { ...l, attivo: false });
    })();
    return () => ferma.abort();
  }, [stackId, liveAttivo, inizioLive]);

  const da = live ? live.inizio : (stato.da ?? predefinita.da);
  const a = live ? live.inizio + LIVE_DURATA : (stato.a ?? predefinita.a);
  const servizio = stato.servizio;
  const scuro = useTemaScuro();
  const t = useT();

  // In live lo storico non si rilegge; dall'ultima lettura servono solo le soglie.
  const risorse = useQuery<RispostaRisorse>(live ? null : `/stacks/${stackId}/risorse?da=${da}&a=${a}`);
  const soglie = risorse.data?.soglie;
  const dati = useMemo<RispostaRisorse | null>(
    () =>
      live && soglie
        ? // Secchio di 5 s: un passo un po' lento non spezza la linea, un container fermo sì.
          { secchio: 5, serie: live.serie, eventi: [], riepilogo: riepilogoLive(live.serie, soglie), soglie }
        : risorse.data,
    [live, soglie, risorse.data],
  );
  // L'ordine dei nomi è quello di `allineaSerie`: fissa il colore di ogni servizio.
  const nomi = useMemo(() => Object.keys(dati?.serie ?? {}).sort(), [dati]);
  const colore = (nome: string) => coloreSerie(nomi.indexOf(nome), scuro);

  const seleziona = (nome: string) => onCambia({ servizio: nome === servizio ? undefined : nome });
  const finestra = (nuova: { da: number; a: number }) => {
    if (nuova.a <= nuova.da) return;
    setLive(null);
    onCambia(nuova);
  };
  // Fermato, il live resta a schermo finché non si sceglie un'altra finestra.
  const commutaLive = () => {
    setErroreLive(null);
    setLive(liveAttivo ? (l) => l && { ...l, attivo: false } : { inizio: ora(), serie: {}, attivo: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(["1h", "6h", "24h", "7g"] as SceltaRapida[]).map((scelta) => (
          <Button key={scelta} className="px-2.5 py-1 text-xs" onClick={() => finestra(finestraRapida(scelta, ora()))}>
            {t.t(`risorse.rapida.${scelta}` as const)}
          </Button>
        ))}
        <Button
          variant={liveAttivo ? "primary" : "secondary"}
          className="px-2.5 py-1 text-xs"
          aria-pressed={liveAttivo}
          title={t.t("risorse.liveAiuto")}
          onClick={commutaLive}
        >
          <span aria-hidden className={cx(liveAttivo && "animate-pulse")}>●</span> {t.t(liveAttivo ? "dettaglio.ferma" : "container.live")}
        </Button>
        <span className="flex flex-wrap items-center gap-1.5 text-xs tenue">
          <input
            type="datetime-local"
            disabled={live !== null}
            aria-label={t.t("risorse.inizio")}
            value={perInput(da)}
            onChange={(e) => e.target.value && finestra({ da: Math.floor(new Date(e.target.value).getTime() / 1000), a })}
            className="rounded-md border bg-[var(--superficie)] px-2 py-1 text-[var(--testo)]"
          />
          →
          <input
            type="datetime-local"
            aria-label={t.t("risorse.fine")}
            disabled={live !== null}
            value={perInput(a)}
            onChange={(e) => e.target.value && finestra({ da, a: Math.floor(new Date(e.target.value).getTime() / 1000) })}
            className="rounded-md border bg-[var(--superficie)] px-2 py-1 text-[var(--testo)]"
          />
        </span>
        {risorse.loading && <Spinner className="tenue" />}
        {live && !liveAttivo && <span className="text-xs tenue">{t.t("risorse.liveTerminato")}</span>}
      </div>

      <ErrorNotice error={erroreLive ?? risorse.error} />

      {dati && nomi.length === 0 && (
        <Empty
          title={t.t(liveAttivo ? "risorse.attesaDati" : "risorse.nessunDato")}
          hint={t.t(liveAttivo ? "risorse.servonoContainer" : "risorse.ogni15")}
        />
      )}

      {dati && nomi.length > 0 && (
        <>
          <div>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
              {GRAFICI.map((g) => (
                <Grafico
                  key={g.metrica}
                  {...g}
                  titolo={t.t(g.titolo)}
                  da={da}
                  a={a}
                  risposta={dati}
                  colore={colore}
                  scuro={scuro}
                  servizio={servizio}
                  onSeleziona={seleziona}
                  onFinestra={finestra}
                />
              ))}
            </div>
            <p className="mt-1 text-xs tenue">{t.t("risorse.graficiAiuto")}</p>
          </div>

          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs tenue">
                <tr>
                  <th className="px-3 py-2 font-medium">{t.t("risorse.servizio")}</th>
                  <th className="px-3 py-2 font-medium">{t.t(live ? "risorse.cpuOra" : "risorse.cpuPicco")}</th>
                  <th className="px-3 py-2 font-medium">{t.t(live ? "risorse.memOra" : "risorse.memPicco")}</th>
                  <th className="px-3 py-2 font-medium">{t.t(live ? "risorse.reteOra" : "risorse.retePicco")}</th>
                  <th className="px-3 py-2 font-medium">{t.t("risorse.eventi")}</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {dati.riepilogo.map((r) => (
                  <tr
                    key={r.serie}
                    onClick={() => seleziona(r.serie)}
                    className={cx(
                      "cursor-pointer border-t hover:bg-black/5 dark:hover:bg-white/5",
                      r.sopraSoglia && "bg-red-500/10 text-red-700 dark:text-red-300",
                      r.serie === servizio && "outline-2 -outline-offset-2 outline-[var(--testo-tenue)]",
                    )}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span aria-hidden style={{ color: colore(r.serie) }}>
                        ●
                      </span>{" "}
                      <span className={cx("font-mono", r.serie === servizio && "font-semibold")}>{r.serie}</span>
                    </td>
                    <td className="px-3 py-2">{numero(r.cpuMax, "%", t)}</td>
                    <td className="px-3 py-2">{numero(r.memPctMax, "%", t)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {numero(r.rxMax === null && r.txMax === null ? null : ((r.rxMax ?? 0) + (r.txMax ?? 0)) / 1024, "KB/s", t)}
                    </td>
                    <td className="px-3 py-2">{r.eventi > 0 ? `▲ ${r.eventi}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {servizio && <LogFinestra key={`${servizio}/${da}/${a}`} stackId={stackId} servizio={servizio} da={da} a={a} />}

      {puòScrivere && <AvvisiDelloStack stackId={stackId} />}
    </div>
  );
}

function Grafico({
  metrica,
  titolo,
  unità,
  da,
  a,
  risposta,
  colore,
  scuro,
  servizio,
  onSeleziona,
  onFinestra,
}: {
  metrica: Metrica;
  titolo: string;
  unità: string;
  da: number;
  a: number;
  risposta: RispostaRisorse;
  colore: (nome: string) => string;
  scuro: boolean;
  servizio: string | undefined;
  onSeleziona: (nome: string) => void;
  onFinestra: (f: { da: number; a: number }) => void;
}) {
  const contenitore = useRef<HTMLDivElement>(null);
  const t = useT();
  // Le funzioni cambiano a ogni render: lette da qui, il grafico non va ricreato per questo.
  const azioni = useRef({ onSeleziona, onFinestra });
  azioni.current = { onSeleziona, onFinestra };

  useEffect(() => {
    const el = contenitore.current;
    if (!el) return;

    const { nomi, dati } = allineaSerie(risposta.serie, risposta.secchio, metrica);
    const stile = getComputedStyle(document.documentElement);
    const tenue = stile.getPropertyValue("--testo-tenue").trim();
    const bordo = stile.getPropertyValue("--bordo").trim();
    const asse = { stroke: tenue, grid: { stroke: bordo, width: 1 }, ticks: { stroke: bordo, width: 1 } };
    let vicina: number | null = null;

    const grafico = new uPlot(
      {
        width: el.clientWidth,
        height: 170,
        legend: { show: false },
        cursor: {
          // La selezione sceglie la finestra di tutta la scheda, non lo zoom di un grafico.
          // `click` va ridato: uPlot sostituisce l'intero oggetto `drag`, e senza il
          // clic che chiude un trascinamento selezionerebbe anche la linea più vicina.
          drag: { x: true, y: false, setScale: false, click: (_u, e) => e.stopPropagation() },
          // Il focus serve solo a sapere quale linea è sotto il cursore.
          focus: { prox: 16 },
        },
        // Alpha 1: con un valore diverso uPlot riscrive l'alpha di ogni serie al
        // passaggio del mouse e l'attenuazione del servizio selezionato andrebbe persa.
        focus: { alpha: 1 },
        scales: {
          // Tutta la finestra, anche dove non ci sono dati: un servizio nato a metà si vede.
          x: { time: true, auto: false, range: [da, a] },
          // La memoria è una percentuale del limite: la scala intera rende visibile quanto manca.
          y: { range: (_u, _min, max) => [0, metrica === "memoria" ? Math.max(100, max) : Math.max(max * 1.1, 1)] },
        },
        axes: [{ ...asse, values: (_u, istanti) => istanti.map((istante) => etichettaTempo(istante, a - da, t.lingua)) }, { ...asse, size: 44, values: (_u, valori) => valori.map((v) => `${v}`) }],
        series: [
          {},
          ...nomi.map((nome) => ({
            label: nome,
            stroke: colore(nome),
            width: nome === servizio ? 2.5 : 1.5,
            alpha: servizio && nome !== servizio ? 0.25 : 1,
            value: (_u: uPlot, v: number | null) => numero(v, unità, t),
          })),
        ],
        hooks: {
          setSeries: [(_u, indice) => void (vicina = indice)],
          setSelect: [
            (u) => {
              if (u.select.width < 2) return;
              const inizio = Math.floor(u.posToVal(u.select.left, "x"));
              const fine = Math.ceil(u.posToVal(u.select.left + u.select.width, "x"));
              u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
              azioni.current.onFinestra({ da: inizio, a: fine });
            },
          ],
          // Arresti e OOM come linee verticali: il picco e la sua conseguenza si leggono insieme.
          draw: [(u) => disegnaEventi(u, risposta.eventi, servizio, tenue, scuro)],
        },
      },
      dati as uPlot.AlignedData,
      el,
    );
    grafico.over.addEventListener("click", () => {
      if (vicina !== null && vicina > 0) azioni.current.onSeleziona(nomi[vicina - 1]!);
    });

    const ridimensiona = new ResizeObserver(() => grafico.setSize({ width: el.clientWidth, height: 170 }));
    ridimensiona.observe(el);

    return () => {
      ridimensiona.disconnect();
      grafico.destroy();
    };
  }, [risposta, metrica, servizio, scuro, da, a, t]);

  return (
    <div className="min-w-0">
      <div className="mb-1 text-xs font-medium tenue">{titolo}</div>
      <div ref={contenitore} className="min-w-0 overflow-hidden" />
    </div>
  );
}

function disegnaEventi(u: uPlot, eventi: EventoContainer[], servizio: string | undefined, tenue: string, scuro: boolean) {
  const { ctx, bbox } = u;
  ctx.save();
  for (const e of eventi) {
    const x = Math.round(u.valToPos(e.ts, "x", true));
    if (x < bbox.left || x > bbox.left + bbox.width) continue;
    // Un fermo voluto da Portman (deploy, stop) e gli avvii sono contesto, non problemi.
    const grave = e.tipo === "oom" || (e.tipo === "fermo" && !e.causatoDaPortman);
    ctx.globalAlpha = servizio && e.serie !== servizio ? 0.25 : 1;
    ctx.strokeStyle = grave ? coloreMarcatore(scuro) : tenue;
    ctx.lineWidth = (grave ? 2 : 1) * uPlot.pxRatio;
    ctx.setLineDash(grave ? [] : [3 * uPlot.pxRatio, 3 * uPlot.pxRatio]);
    ctx.beginPath();
    ctx.moveTo(x, bbox.top);
    ctx.lineTo(x, bbox.top + bbox.height);
    ctx.stroke();
    if (e.tipo === "oom") {
      // L'OOM ha anche un triangolo in cima: è il caso che più spesso spiega un riavvio.
      const l = 5 * uPlot.pxRatio;
      ctx.fillStyle = ctx.strokeStyle;
      ctx.beginPath();
      ctx.moveTo(x - l, bbox.top);
      ctx.lineTo(x + l, bbox.top);
      ctx.lineTo(x, bbox.top + l * 1.5);
      ctx.fill();
    }
  }
  ctx.restore();
}

interface ContainerStack {
  id: string;
  name: string;
  serie: string;
}

interface RigaLog {
  container: string;
  stream: "stdout" | "stderr";
  text: string;
}

// Lo stesso tetto della rotta dei log.
const MAX_RIGHE = 2000;

/**
 * Log del servizio nella finestra, da tutti i suoi container ancora presenti.
 *
 * Con `since`/`until` Docker antepone a ogni riga l'istante RFC3339: serve a
 * mettere in ordine righe di container diversi e a mostrare l'ora locale.
 */
function LogFinestra({ stackId, servizio, da, a }: { stackId: number; servizio: string; da: number; a: number }) {
  const [righe, setRighe] = useState<{ istante: string; ora: string; riga: RigaLog }[] | null>(null);
  const [errore, setErrore] = useState<unknown>(null);
  const t = useT();

  useEffect(() => {
    let attivo = true;
    void (async () => {
      try {
        const { containers } = await api.get<{ containers: ContainerStack[] }>(`/stacks/${stackId}/containers`);
        const blocchi = await Promise.all(
          containers
            .filter((c) => c.serie === servizio)
            .map((c) =>
              api.get<{ logs: RigaLog[] }>(
                `/stacks/${stackId}/logs?container=${encodeURIComponent(c.id)}&since=${da}&until=${a}&tail=${MAX_RIGHE}`,
              ),
            ),
        );
        const tutte = blocchi
          .flatMap((b) => b.logs)
          .map((riga) => {
            const spazio = riga.text.indexOf(" ");
            const istante = spazio > 0 ? riga.text.slice(0, spazio) : "";
            const quando = new Date(istante);
            return {
              istante,
              ora: Number.isNaN(quando.getTime()) ? "" : quando.toLocaleTimeString(t.lingua),
              riga: { ...riga, text: Number.isNaN(quando.getTime()) ? riga.text : riga.text.slice(spazio + 1) },
            };
          })
          // ponytail: ordine lessicale dell'istante RFC3339; Docker toglie gli zeri finali dei nanosecondi, quindi nello stesso secondo l'ordine fra container diversi può sbagliare di poco
          .sort((x, y) => x.istante.localeCompare(y.istante))
          .slice(-MAX_RIGHE);
        if (attivo) setRighe(tutte);
      } catch (causa) {
        if (attivo) setErrore(causa);
      }
    })();
    return () => {
      attivo = false;
    };
  }, [stackId, servizio, da, a, t.lingua]);

  return (
    <section className="min-w-0 space-y-2 border-t pt-3">
      <h2 className="text-sm font-semibold">
        {t.t("container.logDi", { nome: servizio })}{" "}
        <span className="font-normal tenue">
          · {orario(da, t)} → {orario(a, t)}
        </span>
      </h2>
      <ErrorNotice error={errore} />
      {!righe && !errore && <Spinner className="tenue" />}
      {righe && (
        <div className="console corridoio-barra max-h-[28rem] overflow-y-auto rounded-xl px-3 py-1.5 font-mono text-[13px] leading-relaxed">
          {righe.length === 0 && <p className="tenue">{t.t("risorse.nessunLog")}</p>}
          {righe.map(({ ora: quando, riga }, i) => (
            <div key={i} className={cx("whitespace-pre-wrap break-words", riga.stream === "stderr" && "text-rose-300/80")}>
              <span className="mr-2 select-none opacity-50">{quando}</span>
              <TestoAnsi segmenti={segmentaAnsi(riga.text)} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const CAMPI_SOGLIE: { chiave: keyof Soglie; etichetta: Chiave }[] = [
  { chiave: "cpuPct", etichetta: "risorse.sogliaCpu" },
  { chiave: "cpuMinuti", etichetta: "risorse.perMinuti" },
  { chiave: "memPct", etichetta: "risorse.sogliaMemoria" },
  { chiave: "memMinuti", etichetta: "risorse.perMinuti" },
];

/** Soglie di questo stack; un campo vuoto vale la soglia globale delle Impostazioni. */
function AvvisiDelloStack({ stackId }: { stackId: number }) {
  const avvisi = useQuery<AvvisiStack>(`/stacks/${stackId}/avvisi`);
  const [attivi, setAttivi] = useState(true);
  const [campi, setCampi] = useState<Record<keyof Soglie, string>>({ cpuPct: "", cpuMinuti: "", memPct: "", memMinuti: "" });
  const [salvato, setSalvato] = useState(false);
  const t = useT();

  useEffect(() => {
    if (!avvisi.data) return;
    setAttivi(avvisi.data.attivi);
    const s = avvisi.data.soglie;
    setCampi({
      cpuPct: s.cpuPct?.toString() ?? "",
      cpuMinuti: s.cpuMinuti?.toString() ?? "",
      memPct: s.memPct?.toString() ?? "",
      memMinuti: s.memMinuti?.toString() ?? "",
    });
  }, [avvisi.data]);

  const salva = useAction(async () => {
    const soglie = Object.fromEntries(
      Object.entries(campi)
        .filter(([, v]) => v.trim() !== "")
        .map(([k, v]) => [k, Number(v)]),
    );
    await api.put(`/stacks/${stackId}/avvisi`, { attivi, soglie });
    setSalvato(true);
  });

  return (
    <details className="superficie rounded-xl px-4 py-3 text-sm">
      <summary className="cursor-pointer font-medium">{t.t("risorse.avvisi")}</summary>
      <div className="mt-3 space-y-3">
        <ErrorNotice error={avvisi.error ?? salva.error} />
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={attivi} onChange={(e) => {
              setSalvato(false);
              setAttivi(e.target.checked);
            }} />
          {t.t("risorse.inviaAvvisi")}
        </label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {CAMPI_SOGLIE.map(({ chiave, etichetta }) => (
            <label key={chiave} className="block space-y-1">
              <span className="text-xs tenue">{t.t(etichetta)}</span>
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={campi[chiave]}
                placeholder={t.t("verifica.statoPredefinito")}
                disabled={!attivi}
                onChange={(e) => {
                  setSalvato(false);
                  setCampi((c) => ({ ...c, [chiave]: e.target.value }));
                }}
                className="w-full rounded-lg border bg-[var(--superficie)] px-2.5 py-1.5 disabled:opacity-60"
              />
            </label>
          ))}
        </div>
        <p className="text-xs tenue">{t.t("risorse.campoVuoto")}</p>
        <div className="flex items-center gap-3">
          <Button variant="primary" loading={salva.running} onClick={() => void salva.run()}>
            {t.t("comune.salva")}
          </Button>
          {salvato && <span className="text-xs tenue">{t.t("comune.salvato")}</span>}
        </div>
      </div>
    </details>
  );
}
